import { useSwStatus } from "@/contexts/swStatus"
import { useNetwork } from "@/hooks/useNetwork"
import { useMutationState } from "@tanstack/react-query"
import { useGlobalUploadStatus } from "@/hooks/useUploadStatus"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@fieldbook/ui/alert-dialog"

export function UpdateNotification() {
  const {
    updateAvailable,
    ignoreUpdate,
    setIgnoreUpdate,
    updateServiceWorker,
    isUpdating,
  } = useSwStatus()
  const { isOnline } = useNetwork()

  // Hold the prompt back while mutations are still pending (including paused
  // offline ones — they share status: "pending"). Accepting an update busts
  // the persisted query cache, which would discard any unsynced mutations.
  const pendingMutations = useMutationState({
    filters: { status: "pending" },
  })
  const hasPendingMutations = pendingMutations.length > 0
  // Also hold it back while anything is queued for upload: reloading into
  // the new version interrupts uploads in flight, and leaving them to finish
  // first keeps "is it safe to update?" a question the user never has to ask.
  // A queue that isn't moving at all is the exception: the update may be what
  // fixes it, and holding the prompt back would hold it back for good.
  const uploads = useGlobalUploadStatus()
  const hasPendingUploads =
    !uploads.loaded ||
    (uploads.hasPending && uploads.blockedBy !== "no_progress")

  return (
    <AlertDialog
      open={
        updateAvailable &&
        !ignoreUpdate &&
        isOnline &&
        !hasPendingMutations &&
        !hasPendingUploads
      }
    >
      <AlertDialogContent className="space-y-2">
        <AlertDialogHeader className="text-left">
          <AlertDialogTitle>New Version Available!</AlertDialogTitle>
          <AlertDialogDescription>
            Choose to update, or ignore this update for now
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-row justify-end space-x-2">
          <AlertDialogCancel
            className="mt-0"
            onClick={(e) => {
              e.preventDefault()
              setIgnoreUpdate(true)
            }}
          >
            Ignore
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={updateServiceWorker}
            disabled={isUpdating}
          >
            {!isUpdating && "Update"}
            {isUpdating && "Updating..."}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
